import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type HitRect = { left: number; top: number; right: number; bottom: number };

export interface WidgetEmbedPreviewFrameProps {
  publishId: string;
  apiToken?: string;
  settings: unknown;
  siteSettings?: unknown;
  /** overlay は管理画面の上に重ね、ボタンとパネルの上だけクリックを通す。stage はプレビュー枠いっぱい。 */
  variant: 'overlay' | 'stage';
  buttonVisible?: boolean;
  onHideButton?: () => void;
}

const WidgetEmbedPreviewFrame: React.FC<WidgetEmbedPreviewFrameProps> = ({
  publishId,
  apiToken,
  settings,
  siteSettings,
  variant,
  buttonVisible = true,
  onHideButton,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const rectsRef = useRef<HitRect[]>([]);
  const [armed, setArmed] = useState(false);
  const readyRef = useRef(false);

  const src = useMemo(() => {
    const params = new URLSearchParams();
    params.set('publishId', publishId);
    if (apiToken) params.set('apiToken', apiToken);
    return `/widget/embed-preview?${params.toString()}`;
  }, [publishId, apiToken]);

  const draftKey = JSON.stringify({ settings, siteSettings, buttonVisible });

  const postDraft = () => {
    const frameWindow = iframeRef.current?.contentWindow;
    if (!frameWindow) return;
    const plain = (value: unknown) => {
      if (value == null) return null;
      return JSON.parse(JSON.stringify(value));
    };
    frameWindow.postMessage({
      type: 'notreu-preview-draft',
      settings: plain(settings),
      siteSettings: plain(siteSettings),
      buttonVisible,
    }, window.location.origin);
  };

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'notreu-preview-ready') {
        readyRef.current = true;
        postDraft();
      } else if (event.data?.type === 'notreu-preview-hits' && Array.isArray(event.data.rects)) {
        rectsRef.current = event.data.rects;
      } else if (event.data?.type === 'notreu-preview-release') {
        setArmed(false);
      } else if (event.data?.type === 'notreu-preview-hide-button') {
        onHideButton?.();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [draftKey, onHideButton]);

  useEffect(() => {
    if (readyRef.current) postDraft();
  }, [draftKey]);

  useEffect(() => {
    if (variant !== 'overlay') return;
    const onMove = (event: MouseEvent) => {
      const hit = rectsRef.current.some((rect) => (
        event.clientX >= rect.left && event.clientX <= rect.right &&
        event.clientY >= rect.top && event.clientY <= rect.bottom
      ));
      setArmed(hit);
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [variant]);

  const style: React.CSSProperties = variant === 'stage'
    ? {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        border: 'none',
        background: 'transparent',
        zIndex: 5,
      }
    : {
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        border: 'none',
        background: 'transparent',
        zIndex: 999990,
        pointerEvents: armed ? 'auto' : 'none',
      };

  const frame = (
    <iframe
      ref={iframeRef}
      title="notreu widget preview"
      src={src}
      style={style}
    />
  );

  if (variant === 'overlay' && typeof document !== 'undefined') {
    return createPortal(frame, document.body);
  }
  return frame;
};

export default WidgetEmbedPreviewFrame;
